import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, Navigate, useNavigate, useParams } from "react-router-dom";
import { Trash2 } from "lucide-react";
import RecruitmentShell from "../components/RecruitmentShell";
import JobCreateRichTextField from "../components/JobCreateRichTextField";
import { useRejectionReasonTemplates } from "../hooks/useRejectionReasonTemplates";
import { normalizeRichTextStorage } from "../lib/richTextEditor";

const REJECTION_RESPONSE_MAX_CHARS = 2000;

function snapshotFromTemplate(template) {
  return {
    label: String(template?.label ?? ""),
    responseHtml: normalizeRichTextStorage(template?.responseHtml),
  };
}

function draftsEqual(a, b) {
  return a.label === b.label && normalizeRichTextStorage(a.responseHtml) === normalizeRichTextStorage(b.responseHtml);
}

export default function ResponseSettingsRejectionEditPage({ currentUser, setCurrentUser, authToken }) {
  const { templateId } = useParams();
  const navigate = useNavigate();
  const { getById, updateTemplate, removeTemplate, loading, loaded } = useRejectionReasonTemplates(authToken);
  const [saving, setSaving] = useState(false);

  const decodedId = templateId ? decodeURIComponent(templateId) : "";
  const template = decodedId ? getById(decodedId) : undefined;

  const [savedBaseline, setSavedBaseline] = useState(null);
  const [draft, setDraft] = useState(null);

  useEffect(() => {
    if (!template) return;
    const snap = snapshotFromTemplate(template);
    setSavedBaseline(snap);
    setDraft(snap);
  }, [template?.id, template?.label, template?.responseHtml]);

  const isDirty = useMemo(() => {
    if (!draft || !savedBaseline) return false;
    return !draftsEqual(draft, savedBaseline);
  }, [draft, savedBaseline]);

  const handleSave = useCallback(
    async (event) => {
      event.preventDefault();
      if (!template || !draft || !isDirty || saving) return;
      const label = draft.label.trim();
      if (!label) return;
      setSaving(true);
      try {
        await updateTemplate(template.id, {
          label,
          responseHtml: normalizeRichTextStorage(draft.responseHtml),
        });
        navigate("/response-settings");
      } finally {
        setSaving(false);
      }
    },
    [template, draft, isDirty, saving, updateTemplate, navigate]
  );

  const handleReset = useCallback(() => {
    if (savedBaseline) setDraft(savedBaseline);
  }, [savedBaseline]);

  const handleCancel = useCallback(() => {
    navigate("/response-settings");
  }, [navigate]);

  if (loaded && !loading && !template) {
    return <Navigate to="/response-settings" replace />;
  }

  if (loading || !template) {
    return null;
  }

  if (!draft || !savedBaseline) {
    return null;
  }

  const pageTitle = draft.label.trim() || (template.isCustom ? "New rejection reason" : "Rejection response");

  const handleRemove = async () => {
    if (!template.isCustom || saving) return;
    setSaving(true);
    try {
      await removeTemplate(template.id);
      navigate("/response-settings");
    } finally {
      setSaving(false);
    }
  };

  return (
    <RecruitmentShell currentUser={currentUser} setCurrentUser={setCurrentUser}>
      <main className="nd1-shell nd1-recruitment-profile-main jw-recruitment-main recruitment-profile-page response-settings-page response-settings-edit-page">
        <div className="recruitment-profile-shell nd1-recruitment-profile-shell">
          <header className="recruitment-profile-masthead nd1-recruitment-profile-masthead">
            <Link className="back-link nd1-recruitment-profile-back" to="/response-settings">
              &larr; Back to response settings
            </Link>
            <p className="eyebrow nd1-label nd1-recruitment-profile-eyebrow">Rejection notifications</p>
            <h1>{pageTitle}</h1>
            <p className="subtitle">
              Message shown when you reject with this reason and send a notification.
            </p>
          </header>

          <div className="recruitment-profile-content profile-manage-card nd1-recruitment-profile-content">
            <form
              id="response-settings-rejection-edit-form"
              className="recruitment-profile-stack recruitment-profile-stack--settings response-settings-stack"
              onSubmit={handleSave}
            >
              <section className="recruitment-profile-surface response-settings-rejection-edit nd1-recruitment-settings-fieldset">
                <label className="response-settings-rejection-label-field">
                  <span className="job-search-label">Rejection name</span>
                  <input
                    type="text"
                    className="response-settings-rejection-label-input"
                    value={draft.label}
                    placeholder={template.isCustom ? "e.g. Overqualified for this level" : "Rejection reason name"}
                    maxLength={120}
                    onChange={(e) => setDraft((prev) => ({ ...prev, label: e.target.value }))}
                  />
                </label>

                <JobCreateRichTextField
                  value={draft.responseHtml}
                  onChange={(e) =>
                    setDraft((prev) => ({ ...prev, responseHtml: normalizeRichTextStorage(e.target.value) }))
                  }
                  placeholder="Write your rejection message…"
                  maxChars={REJECTION_RESPONSE_MAX_CHARS}
                  ariaLabel="Rejection message"
                />

                {template.isCustom ? (
                  <button
                    type="button"
                    className="small-outline-button response-settings-rejection-remove nd1-recruitment-settings-remove-btn"
                    onClick={handleRemove}
                  >
                    <Trash2 size={16} strokeWidth={1.75} aria-hidden />
                    Remove this reason
                  </button>
                ) : null}
              </section>
            </form>

            <div
              className={`job-actions-float nd1-recruitment-profile-footer${isDirty ? " job-actions-float--unsaved" : ""}`}
              role="toolbar"
              aria-label={
                isDirty ? "Rejection response save actions, unsaved changes" : "Rejection response save actions"
              }
            >
              {isDirty ? (
                <p className="profile-manage-unsaved-hint" role="status">
                  Changes are not saved.
                </p>
              ) : null}
              <div className="job-actions-float-row">
                <div className="job-actions-buttons">
                  <button
                    type="submit"
                    form="response-settings-rejection-edit-form"
                    className="job-action-btn primary"
                    disabled={!isDirty}
                  >
                    Save changes
                  </button>
                  <button
                    type="button"
                    className="job-action-btn"
                    disabled={!isDirty}
                    aria-label="Discard unsaved changes"
                    onClick={handleReset}
                  >
                    Reset
                  </button>
                  <button type="button" className="job-action-btn" onClick={handleCancel}>
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </RecruitmentShell>
  );
}
